import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Button, Card, message, Modal, Descriptions, Tag, Input } from "antd";
import { DownOutlined } from "@ant-design/icons";
import { CustomTable } from "@/hocComponents";
import http from "@/utils/http";
import api from "@/utils/api";
import commonFn from "@/utils/commonFn";

export default function PropertyBreakdown({
  supervisorId,
  supervisorName,
  ownerRecord,
  visible,
  onClose,
  month, // 新增：传入的月份参数
  title, // 新增：标题
}) {
  const [propertyData, setPropertyData] = useState([]);
  const [propertyLoading, setPropertyLoading] = useState(false);
  const [tableHead, setTableHead] = useState({});
  const [searchValue, setSearchValue] = useState("");
  const [pagination, setPagination] = useState({
    page: 1,
    count: 20,
    total: 0,
    totalPages: 0,
  });
  const isFetching = useRef(false);
  const debounceTimerRef = useRef(null);
  const lastSearchValueRef = useRef("");
  const requestIdRef = useRef(0);

  const transformData = (details) => {
    if (!details || !Array.isArray(details)) return [];

    return details.map((item) => ({
      key: `${item.propId}`,
      propId: item.propId,
      propertyName: item.propertyName || '',
      propertyType: item.propertyType || '',
      units: item.units || 0,
      current: item.current || 0,
      days30: item.days30 || 0,
      days60: item.days60 || 0,
      days90Plus: item.days90Plus || 0,
      total: item.total || 0,
      pctOfTotalDelinquency: item.pctOfTotalDelinquency
        ? `${Number(item.pctOfTotalDelinquency).toFixed(2)}%`
        : '',
      grossRent: item.grossRent || 0,
      invoicedRent: item.invoicedRent || 0,
      vacancyLossRate: item.vacancyLossRate || 0,
      securityDeposit: item.securityDeposit || 0,
      lateCharge: item.lateCharge || 0,
      utilities: item.utilities || 0,
      other: item.other || 0,
      advanceRent: item.advanceRent || 0,
      backBalance: item.backBalance || 0,
      pastTenant: item.pastTenant || 0,
      totalCashReceived: item.totalCashReceived || 0,
      rawData: item,
    }));
  };

  const tableFilterFn = (event, key, type = "Input") => {
    commonFn.tableFilterFn({
      event,
      key,
      type,
      setTableHead,
      getTableListFn: (page, pageSize, tableHeadParams) => {
        const searchValue = tableHeadParams?.propertyName || null;
        fetchTableData(searchValue, page || 1, pageSize || pagination.count);
      },
      pageSize: pagination.count
    });
  };

  const columns = [
    {
      title: "Property Name",
      dataIndex: "propertyName",
      titleType: "Input",
      width: 280,
      onChange: (e) => tableFilterFn(e, "propertyName"),
    },
    {
      title: "Property Type",
      dataIndex: "propertyType",
      width: 120,
    },
    {
      title: "Gross Rent/Invoiced Rent",
      dataIndex: "grossRent",
      width: 180,
      render: (text, record) => {
        const grossRent = record.grossRent || 0;
        const invoicedRent = record.invoicedRent || 0;
        const grossRentFormatted = grossRent > 0 ? commonFn.amountToDollarBracketFn(grossRent) : '';
        const invoicedRentFormatted = invoicedRent > 0 ? commonFn.amountToDollarBracketFn(invoicedRent) : '';

        if (!grossRentFormatted && !invoicedRentFormatted) {
          return '';
        }

        const parts = [grossRentFormatted, invoicedRentFormatted].filter(Boolean);
        return <span>{parts.join(' / ')}</span>;
      },
    },
    {
      title: "Vacancy Loss",
      dataIndex: "vacancyLossRate",
      width: 120,
      render: (text, record) => {
        const value = record.vacancyLossRate;
        if (value === null || value === undefined || value === '' || value === 0) return '';
        const num = Number(value);
        if (!isFinite(num)) return '';
        return `${(num * 100).toFixed(2)}%`;
      },
    },
    {
      title: "Security Dep.",
      dataIndex: "securityDeposit",
      width: 120,
      render: (text, record) => {
        const value = record.securityDeposit || 0;
        return value > 0 ? commonFn.amountToDollarBracketFn(value) : '';
      },
    },
    {
      title: "Late Charge",
      dataIndex: "lateCharge",
      width: 120,
      render: (text, record) => {
        const value = record.lateCharge || 0;
        return value > 0 ? commonFn.amountToDollarBracketFn(value) : '';
      },
    },
    {
      title: "Utilities",
      dataIndex: "utilities",
      width: 120,
      render: (text, record) => {
        const value = record.utilities || 0;
        return value > 0 ? commonFn.amountToDollarBracketFn(value) : '';
      },
    },
    {
      title: "Other",
      dataIndex: "other",
      width: 120,
      render: (text, record) => {
        const value = record.other || 0;
        return value > 0 ? commonFn.amountToDollarBracketFn(value) : '';
      },
    },
    {
      title: "Advance Rent",
      dataIndex: "advanceRent",
      width: 120,
      render: (text, record) => {
        const value = record.advanceRent || 0;
        return value > 0 ? commonFn.amountToDollarBracketFn(value) : '';
      },
    },
    {
      title: "Back Balance",
      dataIndex: "backBalance",
      width: 120,
      render: (text, record) => {
        const value = record.backBalance || 0;
        return value > 0 ? commonFn.amountToDollarBracketFn(value) : '';
      },
    },
    {
      title: "Past Tenant",
      dataIndex: "pastTenant",
      width: 120,
      render: (text, record) => {
        const value = record.pastTenant || 0;
        return value > 0 ? commonFn.amountToDollarBracketFn(value) : '';
      },
    },
    {
      title: "Total Cash Received",
      dataIndex: "totalCashReceived",
      width: 160,
      render: (text, record) => {
        const value = record.totalCashReceived || 0;
        return value > 0 ? commonFn.amountToDollarBracketFn(value) : '';
      },
    },
  ];

  const fetchTableData = useCallback(async (searchValue = null, page = 1, count = 20) => {
    if (isFetching.current) return;
    isFetching.current = true;
    setPropertyLoading(true);

    try {
      const params = {
        page: page,
        count: count,
        month: month, // 使用传入的 month 参数
      };

      if (searchValue) {
        params.propertyName = searchValue;
      }
      const response = await http.post(api.getCollectionShowList, params);

      if (response?.code === "1") {
        const { details, total, totalPages, page: resPage, count: resCount } = response?.data || {};
        const transformedData = transformData(details || []);
        setPropertyData(transformedData);
        setPagination({
          page: resPage || page,
          count: resCount || count,
          total: total || 0,
          totalPages: totalPages || 0,
        });
      } else {
        setPropertyData([]);
        message.error(response?.msg);
      }
    } catch (e) {
      setPropertyData([]);
    } finally {
      setPropertyLoading(false);
      isFetching.current = false;
    }
  }, [month]);

  const loadInitialData = useCallback((page = 1, pageSize = 20) => {
    const searchVal = searchValue || null;
    fetchTableData(searchVal, page, pageSize);
  }, [searchValue, fetchTableData]);

  // 监听搜索值变化，防抖处理
  useEffect(() => {
    if (!visible) return;

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      const currentSearch = searchValue || null;
      if (currentSearch !== lastSearchValueRef.current) {
        lastSearchValueRef.current = currentSearch;
        loadInitialData(1, pagination.count);
      }
    }, 500);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, [searchValue, visible, loadInitialData, pagination.count]);

  useEffect(() => {
    if (visible && month) {
      setTableHead({});
      setSearchValue("");
      lastSearchValueRef.current = "";
      requestIdRef.current = 0;
      loadInitialData();
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, [visible, month, loadInitialData]);

  const displaySupervisorName = title || supervisorName || 'Previous Month Data';

  if (!visible) return null;

  return (
    <div className="property-bottom-panel property-bottom-panel-open">
      <Button
        type="default"
        shape="circle"
        icon={<DownOutlined />}
        className="property-collapse-btn"
        onClick={onClose}
      />

      <Card className="section-card property-card">
        <div className="section-card-header-property">
          <div>
            <div className="section-card-title">Property Detail - {displaySupervisorName}</div>
            {month && (
              <div className="property-modal-meta">
                <span className="property-modal-meta-owner">
                  Month: {month}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="section-card-table">
          <CustomTable
            tableLoading={propertyLoading}
            tableList={propertyData}
            columns={columns}
            pagination={{
              current: pagination.page,
              pageSize: pagination.count,
              total: pagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `Total ${total} items`,
              onChange: (page, pageSize) => {
                loadInitialData(page, pageSize);
              },
              onShowSizeChange: (current, size) => {
                loadInitialData(1, size);
              },
            }}
            tableScrollY="450px"
            setTableHead={setTableHead}
            getTableListFn={loadInitialData}
          />
        </div>
      </Card>
    </div>
  );
}